import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Filter, 
  TrendingUp, 
  Activity, 
  Target, 
  Thermometer,
  Calculator,
  Eye,
  EyeOff,
  Settings,
  Layers
} from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';
import { KPIDashboard } from './KPIDashboard';
import { FilterBar } from './FilterBar';
import { GapInsights } from './GapInsights';
import { MarketThermometerReport } from './MarketThermometerReport';
import { ShortEntryThresholdCalculator } from './ShortEntryThresholdCalculator';
import { DataTable } from './DataTable';
import { CalculatedMetrics, KPIMetrics, FilterState } from '../types';

interface DashboardLayoutProps {
  calculatedData: CalculatedMetrics[];
  filteredData: CalculatedMetrics[];
  kpis: KPIMetrics;
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

const STORAGE_KEY = 'dashboard_section_visibility';

const loadSectionVisibility = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading section visibility:', error);
  }
  return {
    kpis: true,
    filters: true,
    insights: true,
    thermometer: false,
    calculator: false,
    dataTable: true
  };
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  calculatedData,
  filteredData,
  kpis,
  filters,
  onFiltersChange
}) => {
  const [sectionVisibility, setSectionVisibility] = useState(loadSectionVisibility);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sectionVisibility));
    } catch (error) {
      console.error('Error saving section visibility:', error);
    }
  }, [sectionVisibility]);

  const toggleSection = (section: keyof typeof sectionVisibility) => {
    setSectionVisibility(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleAllSections = (expand: boolean) => {
    setSectionVisibility({
      kpis: expand,
      filters: expand,
      insights: expand,
      thermometer: expand,
      calculator: expand,
      dataTable: expand
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Dashboard Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-400/10 rounded-lg">
            <Layers className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-white">Organizzazione Dashboard</h2>
            <p className="text-xs sm:text-sm text-gray-400">Gestisci la visibilità delle sezioni</p>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={() => toggleAllSections(true)}
            className="flex items-center space-x-1.5 sm:space-x-2 px-3 py-2 bg-green-600 hover:bg-green-500 active:bg-green-400 text-white rounded-lg transition-all duration-150 text-xs sm:text-sm touch-manipulation min-h-[44px] sm:min-h-0"
          >
            <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Espandi Tutto</span>
          </button>
          <button
            onClick={() => toggleAllSections(false)}
            className="flex items-center space-x-1.5 sm:space-x-2 px-3 py-2 bg-gray-600 hover:bg-gray-500 active:bg-gray-400 text-white rounded-lg transition-all duration-150 text-xs sm:text-sm touch-manipulation min-h-[44px] sm:min-h-0"
          >
            <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>Comprimi Tutto</span>
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <CollapsibleSection
        title="Filtri e Ricerca"
        subtitle="Filtra i dati per analisi specifiche"
        icon={<Filter className="h-5 w-5 text-green-400" />}
        defaultExpanded={sectionVisibility.filters}
        badge={`${filteredData.length} risultati`}
        variant="default"
      >
        <FilterBar 
          filters={filters}
          onFiltersChange={onFiltersChange}
          totalResults={filteredData.length}
        />
      </CollapsibleSection>

      {/* KPI Dashboard Section */}
      <CollapsibleSection
        title="Metriche Chiave (KPI)"
        subtitle="Panoramica delle metriche principali del dataset"
        icon={<BarChart3 className="h-5 w-5 text-blue-400" />}
        defaultExpanded={sectionVisibility.kpis}
        badge={`${kpis.numberOfGaps} gap`}
        variant="premium"
      >
        <KPIDashboard metrics={kpis} filters={filters} />
      </CollapsibleSection>

      {/* Insights Section */}
      <CollapsibleSection
        title="Insights di Trading"
        subtitle="Analisi automatica e suggerimenti operativi"
        icon={<TrendingUp className="h-5 w-5 text-yellow-400" />}
        defaultExpanded={sectionVisibility.insights}
        variant="premium"
      >
        <GapInsights 
          calculatedData={filteredData}
          kpis={kpis}
        />
      </CollapsibleSection>

      {/* Market Thermometer Section */}
      <CollapsibleSection
        title="Termometro del Mercato"
        subtitle="Report delle condizioni di mercato con filtri personalizzabili"
        icon={<Thermometer className="h-5 w-5 text-orange-400" />}
        defaultExpanded={sectionVisibility.thermometer}
        variant="compact"
      >
        <MarketThermometerReport data={calculatedData} />
      </CollapsibleSection>

      {/* Spike Analysis Calculator Section */}
      <CollapsibleSection
        title="Spike Analysis Dashboard"
        subtitle="Analisi completa dei pattern di spike per strategie di short selling"
        icon={<Calculator className="h-5 w-5 text-red-400" />}
        defaultExpanded={sectionVisibility.calculator}
        variant="compact"
      >
        <ShortEntryThresholdCalculator calculatedData={calculatedData} />
      </CollapsibleSection>

      {/* Data Table Section */}
      <CollapsibleSection
        title="Tabella Dati Completa"
        subtitle="Visualizzazione dettagliata di tutti i gap filtrati"
        icon={<Activity className="h-5 w-5 text-purple-400" />}
        defaultExpanded={sectionVisibility.dataTable}
        badge={`${filteredData.length} righe`}
        variant="default"
      >
        <DataTable data={filteredData} />
      </CollapsibleSection>
    </div>
  );
};